import React, { memo, useCallback, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import FileOpenIcon from "@mui/icons-material/FileOpen";
import { func } from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import IconButtonBadge from "../../../../../shared/ui/IconButtonBadge/IconButtonBadge";
import checkValidate from "./utils/checkValidate";
import { usePostGeoZonesImportMutation } from "../../../../../entities/geoZones/geoZone/redux/geoZone.api";

function ImportGeoZoneButton({ getGetGeoZonesFunc }) {
  const [postGeoZones] = usePostGeoZonesImportMutation();
  const fileZipFileRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isReplace, setIsReplace] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleClick = () => {
    if (fileZipFileRef.current.click) {
      fileZipFileRef.current.click();
    } else {
      toast.error("Ошибка при обработке файла, попробуйте еще раз");
    }
  };

  const handleFileChange = useCallback((event) => {
    const file = event.target.files[0];

    if (checkValidate(file)) {
      setSelectedFile(file);
      setIsReplace(false);
      setDialogOpen(true);
    }

    // eslint-disable-next-line no-param-reassign
    event.target.value = null;
  }, []);

  const handleCancel = useCallback(() => {
    setDialogOpen(false);
    setSelectedFile(null);
    setIsReplace(false);
  }, []);

  const handleImport = useCallback(() => {
    if (!selectedFile) return;

    setLoading(true);
    const formData = new FormData();
    formData.append("file", selectedFile, selectedFile.name);
    formData.append("isReplace", isReplace ? "true" : "false");

    postGeoZones(formData)
      .unwrap()
      .then(() => {
        getGetGeoZonesFunc();
        setDialogOpen(false);
        setSelectedFile(null);
        setIsReplace(false);
      })
      .catch(() => {
        toast.error("Ошибка при импорте геозон");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedFile, isReplace, postGeoZones, getGetGeoZonesFunc]);

  return (
    <>
      <input
        ref={fileZipFileRef}
        onChange={handleFileChange}
        accept=".csv,.kml,.kml.gz"
        style={{ display: 'none' }}
        id="raised-button-file3"
        multiple
        type="file"
      />
      <IconButtonBadge
        title="Импорт геозон из файлов CSV, KML"
        onClick={handleClick}
      >
        <FileOpenIcon />
      </IconButtonBadge>
      <Dialog open={dialogOpen} onClose={handleCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Импорт файла с геозонами</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы хотите импортировать информацию о геозонах из файла {selectedFile?.name}?
          </DialogContentText>
          <FormControlLabel
            control={<Switch checked={isReplace} onChange={() => setIsReplace(!isReplace)} />}
            label="Удалить предыдущие неиспользуемые геозоны"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={loading}>Отменить</Button>
          <Button onClick={handleImport} variant="contained" disabled={loading}>Импортировать</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

ImportGeoZoneButton.propTypes = {
  getGetGeoZonesFunc: func.isRequired,
};

export default memo(ImportGeoZoneButton);
